import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel';
import httpClient from '../services/httpClient';

type PlatformCard = {
  id: number;
  name: string;
  platforms: string[];
  imageUrl?: string | null;
  isSharing: boolean;
  position: number;
};

const isPlayStationCard = (card: PlatformCard) =>
  card.platforms.some(platform => /^ps(?:4|5)$/i.test(platform) || /playstation/i.test(platform))
  || /playstation|ps\s*(?:store|4|5)/i.test(card.name);

const cardHref = (card: PlatformCard) => {
  if (card.isSharing) return '/catalog?sharing=true';
  if (isPlayStationCard(card)) return '/playstation-store';

  return `/games/request?platform=${encodeURIComponent(card.name)}`;
};

const PlatformCarousel = () => {
  const [cards, setCards] = useState<PlatformCard[]>([]);

  useEffect(() => {
    let cancelled = false;
    httpClient
      .get<PlatformCard[]>('/api/PlatformCards')
      .then(({ data }) => {
        if (cancelled) return;
        const list = Array.isArray(data) ? data : [];
        setCards(list.filter(c => c.imageUrl || c.isSharing));
      })
      .catch(() => { /* карусель необязательна — молча скрываем при ошибке */ });
    return () => { cancelled = true; };
  }, []);

  return (
    <section className="mb-12">
      <div className="mb-5">
        <h2 className="text-2xl font-semibold text-foreground sm:text-3xl">Игры</h2>
      </div>

      <Carousel opts={{ align: 'start', slidesToScroll: 'auto' }} className="w-full">
        <CarouselContent className="-ml-2 sm:-ml-[18px]">
          {cards.map((c) => (
            <CarouselItem
              key={c.id}
              className="pl-2 sm:pl-[18px] basis-1/2 sm:basis-1/3 md:basis-1/4 lg:basis-1/5 xl:basis-1/6"
            >
              <Link to={cardHref(c)} className="block">
                <article className="group overflow-hidden rounded-2xl border border-foreground/10 bg-[hsl(var(--card-tinted))] transition-all duration-300 hover:-translate-y-1 hover:border-foreground/20">
                  <div className={`aspect-square overflow-hidden ${c.isSharing && !c.imageUrl ? 'flex items-center justify-center bg-gradient-to-br from-violet-700 via-fuchsia-600 to-rose-500' : ''}`}>
                    {c.imageUrl ? <img
                      src={c.imageUrl ?? ''}
                      alt={c.name}
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
                      loading="lazy"
                    /> : <svg className="h-20 w-20 text-white/90" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8.7 12.8 15.3 9m-6.6 2.2 6.6 3.8M18 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM6 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Zm12 7a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" />
                    </svg>}
                  </div>
                  <div className="p-3 text-center">
                    <h3 className="truncate text-base font-medium text-foreground">{c.name}</h3>
                  </div>
                </article>
              </Link>
            </CarouselItem>
          ))}
        </CarouselContent>
        <CarouselPrevious className="hidden md:flex border-foreground/20 bg-foreground/10 text-foreground hover:bg-foreground/20 hover:text-foreground" />
        <CarouselNext className="hidden md:flex border-foreground/20 bg-foreground/10 text-foreground hover:bg-foreground/20 hover:text-foreground" />
      </Carousel>
    </section>
  );
};

export default PlatformCarousel;
